import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {tap} from 'rxjs/operators';
import {Auth} from '../actions/auth.actions';
import {Labour} from '../models/labour/labour.model';
import {JobLabour} from '../models/labour/jobLabour.model';
import {LabourService} from '../services/labour.service';
import {LabourActions} from '../actions/labour.actions';
import ClearState = Auth.ClearState;
import GetLabours = LabourActions.GetLabours;
import GetLabour = LabourActions.GetLabour;
import CreateLabour = LabourActions.CreateLabour;
import UpdateLabour = LabourActions.UpdateLabour;
import DeleteLabour = LabourActions.DeleteLabour;
import GetJobLabour = LabourActions.GetJobLabour;
import UpdateJobLabour = LabourActions.UpdateJobLabour;

export interface LabourStateModel {
  labour: Array<Labour>;
  currentLabour: Labour | null;
  labourGroups: Array<string>;
  jobLabour: JobLabour | null;
}

@State<LabourStateModel>({
  name: 'labour',
  defaults: {
    labour: [],
    currentLabour: null,
    labourGroups: [],
    jobLabour: null
  }
})
@Injectable()
export class LabourState {
  constructor(private labourService: LabourService) {
  }

  @Selector()
  static labours(state: LabourStateModel): Array<Labour> {
    return state.labour;
  }

  @Selector()
  static labour(state: LabourStateModel): Labour {
    return state.currentLabour;
  }

  @Selector()
  static labourGroups(state: LabourStateModel): Array<string> {
    return state.labourGroups;
  }


  @Selector()
  static jobLabour(state: LabourStateModel): JobLabour {
    return state.jobLabour;
  }

  @Action(GetLabours)
  getLabours(ctx: StateContext<LabourStateModel>, action: GetLabours): Observable<Labour[]> {
    if (!action.force) {
      const labour = ctx.getState().labour;
      if (labour.length > 0) {
        return of(labour);
      }
    }
    return this.labourService.getLabours().pipe(
      tap(res => {
        ctx.patchState({
          labour: res,
          labourGroups: this.getAllLabourGroups(res)
        });
      }),
    );
  }

  private getAllLabourGroups(labour: Labour[]): string[] {
    const labourGroupMapping = {};
    labour.forEach(l => {
      labourGroupMapping[l.group] = true;
    });

    return Object.keys(labourGroupMapping).sort();
  }

  @Action(GetLabour)
  getLabour(ctx: StateContext<LabourStateModel>, action: GetLabour): Observable<Labour> {
    if (!action.force) {
      const labour = ctx.getState().currentLabour;
      if (labour && labour.id === action.id) {
        return of(labour);
      }

      const cachedLabour = ctx.getState().labour.find(p => p.id === action.id);
      if (cachedLabour) {
        ctx.patchState({
          currentLabour: cachedLabour
        });
        return of(cachedLabour);
      }
    }
    return this.labourService.getLabour(action.id).pipe(
      tap(labour => {
        ctx.patchState({
          currentLabour: labour
        });
      })
    );
  }

  @Action(CreateLabour)
  createLabour(ctx: StateContext<LabourStateModel>, action: CreateLabour): Observable<Labour> {
    return this.labourService.createLabour(action.labour).pipe(
      tap(labour => {
        ctx.patchState({
          currentLabour: labour
        });
      })
    );
  }

  @Action(UpdateLabour)
  updateLabour(ctx: StateContext<LabourStateModel>, action: UpdateLabour): Observable<Labour> {
    return this.labourService.updateLabour(action.labour).pipe(
      tap(labour => {
        ctx.patchState({
          currentLabour: labour
        });
      })
    );
  }

  @Action(DeleteLabour)
  deleteLabour(ctx: StateContext<LabourStateModel>, action: DeleteLabour): Observable<Labour> {
    const labours = ctx.getState().labour;
    const index = labours.indexOf(action.labour);
    return this.labourService.deleteLabour(action.labour.id).pipe(
      tap(labour => {
        ctx.patchState({
          currentLabour: null,
          labour: [
            ...labours.slice(0, index),
            ...index === labours.length - 1 ? [] : labours.slice(index + 1)
          ]
        });
      })
    );
  }

  @Action(GetJobLabour)
  getJobLabour(ctx: StateContext<LabourStateModel>, action: GetJobLabour): Observable<JobLabour> {
    return this.labourService.getJobLabour(action.id).pipe(
      tap(res => {
        ctx.patchState({
          jobLabour: res
        });
      }),
    );
  }

  @Action(UpdateJobLabour)
  updateJobLabour(ctx: StateContext<LabourStateModel>, action: UpdateJobLabour): Observable<JobLabour> {
    return this.labourService.updateJobLabour(action.jobLabour).pipe(
      tap(res => {
        ctx.patchState({
          jobLabour: res
        });
      }),
    );
  }

  @Action(ClearState)
  clearState(ctx: StateContext<LabourStateModel>): void {
    ctx.setState({
      labour: [],
      currentLabour: null,
      labourGroups: [],
      jobLabour: null
    });
  }
}
